import axiosInstance from "../axios-instance";
import { uploadIxdToStagingSlot } from "../ixd-upload/policy";
import type {
  IXDPreviewResponse,
  IxdDownloadFile,
  IxdNoteChecklistResponse,
  ListIxdReportsResponse,
  SaveIxdChangeItem,
  SaveIxdChangesRequest,
  UploadDartFilingResponse,
  UploadIxdPreviewResponse,
} from "./types";

export type {
  IXDPreviewResponse,
  IxdDownloadFile,
  ListIxdReportsResponse,
  SaveIxdChangeItem,
  UploadDartFilingResponse,
  UploadIxdPreviewResponse,
} from "./types";

/**
 * IXD 파일 업로드 → DB+GCS 저장 → reportId 반환
 *
 * 스테이징 슬롯에 올린 뒤 uploadId 로 완료를 호출한다. 분할 업로드는 하지 않는다 —
 * 기본 경로는 서버를 안 거쳐 요청 크기 상한이 없고, 폴백은 그 상한이 없는 로컬에서만 돈다.
 */
export const uploadIxdPreview = async (
  file: File,
): Promise<UploadIxdPreviewResponse> => {
  const uploadId = await uploadIxdToStagingSlot(file);

  const response = await axiosInstance.post("/v1/ixd-editor/upload/complete", {
    uploadId,
    filename: file.name,
  });
  return response.data;
};

/**
 * reportId로 미리보기 데이터 조회
 */
export const getIxdPreview = async (
  reportId: string,
): Promise<IXDPreviewResponse> => {
  const response = await axiosInstance.get(
    `/v1/ixd-editor/report-preview/${reportId}`,
    { headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } },
  );
  const data = response.data;

  // 백엔드가 단일 JSON을 chunked(Content-Length 없음)로 흘린다. 스트림이 중간에
  // 끊기면(프록시/Cloud Run 타임아웃, 직렬화 중 예외 → 헤더는 이미 200) truncated
  // body가 오는데, axios는 silentJSONParsing 기본값 탓에 JSON.parse 실패를 안 던지고
  // raw string을 그대로 반환한다. 그 string을 그냥 넘기면 렌더에서 터지니 여기서 막는다.
  if (
    !data ||
    !Array.isArray(data.consolidatedChunks) ||
    !Array.isArray(data.separateChunks)
  ) {
    throw new Error(
      "미리보기 응답이 완전히 전송되지 않았습니다. 다시 시도해 주세요.",
    );
  }
  return data as IXDPreviewResponse;
};

/**
 * DART 공시 XBRL(zip) 업로드 → 버킷 저장 → filingId 반환.
 *
 * IXD 와 달리 리포트를 만들지 않는다 — 편집기 산출물이 아니라 공시본이라 값 편집
 * 경로가 없다. 대사 화면이 보여주지 못하는(어느 DSD chunk 에도 매핑되지 않은) 표를
 * 훑어보는 읽기 전용 열람용이다.
 */
export const uploadDartFilingPreview = async (
  file: File,
): Promise<UploadDartFilingResponse> => {
  const uploadId = await uploadIxdToStagingSlot(file);

  const response = await axiosInstance.post(
    "/v1/ixd-editor/filings/upload/complete",
    { uploadId, filename: file.name },
  );
  return response.data;
};

/** filingId 로 읽기 전용 프리뷰 조회. 스트림 절단 방어는 getIxdPreview 와 같다. */
export const getDartFilingPreview = async (
  filingId: string,
): Promise<IXDPreviewResponse> => {
  const response = await axiosInstance.get(
    `/v1/ixd-editor/filings/${filingId}/preview`,
    { headers: { "Cache-Control": "no-cache", Pragma: "no-cache" } },
  );
  const data = response.data;
  if (
    !data ||
    !Array.isArray(data.consolidatedChunks) ||
    !Array.isArray(data.separateChunks)
  ) {
    throw new Error(
      "미리보기 응답이 완전히 전송되지 않았습니다. 다시 시도해 주세요.",
    );
  }
  return data as IXDPreviewResponse;
};

/**
 * 업로드된 IXD 리포트 목록 조회
 */
export const getIxdReports = async (): Promise<ListIxdReportsResponse> => {
  const response = await axiosInstance.get("/v1/ixd-editor/reports");
  return response.data;
};

/**
 * reportId 기반 수정된 IXD 다운로드용 signed URL 발급 (현재 DB 상태 반영)
 */
export const downloadModifiedIxd = async (
  reportId: string,
): Promise<IxdDownloadFile> => {
  const response = await axiosInstance.post(
    `/v1/ixd-editor/report-preview/${reportId}/download`,
  );
  return response.data as IxdDownloadFile;
};

/**
 * 수정사항 저장 (204 No Content)
 */
export const saveIxdChanges = async (
  reportId: string,
  changes: SaveIxdChangeItem[],
): Promise<void> => {
  const body: SaveIxdChangesRequest = { changes };

  const url = `/v1/ixd-editor/report-preview/${reportId}`;
  await axiosInstance.patch(url, body);
};

/** 저장된 IXD 주석의 DART 작성 지침 체크리스트 검증. 요청 시 계산되며 DB 현재 값 기준이다. */
export const getIxdNoteChecklist = async (
  reportId: string,
): Promise<IxdNoteChecklistResponse> => {
  const response = await axiosInstance.get(
    `/v1/ixd-editor/report-note-checklist/${reportId}`,
  );
  return response.data;
};
